(function () {
	'use strict';

	angular
		.module('smb-app')
		.factory('smbContentUploadService',
		[
			'$http',
			'$q',
			'pxUriPathService',
			UploadService
		]);

	function UploadService($http, $q, pxUriPathService) {

		var chunkSize = 1024 * 512;

		return {

			/**
			* @name uploadFile
			* @kind function
			* @description
			* Uploads a file. This method will chunk the file and upload
			* incrementally so as not to avoid request timeouts and content length 
			* restrictions
			*
			* @param {number} folderId - The folder to which this document should be posted
			* @param {Object} file - A file object which was generated by a browser events
			* 
			* @returns {Deferred} A promise that will resolve when the file
			* has finished uploading, or a rejection with an error if a problem
			* occurs during the upload process.
			*
			* @example
			*       function onFileDrop(e) {
			*           e.preventDefault();
			*           e.stopPropagation();
			*
			*           var files = e.originalEvent.dataTransfer.files;
			*                           
			*           pxUpload.uploadFile(folderId, files[0])
			*               .then(function() {
			*                   console.log('success!');
			*               })
			*               .catch(function(){
			*                   console.log('error!');
			*               })
			*       }
			*/
			uploadFile: uploadFile,

			/**
			 * @ngdoc method
			 * @name uploadRevision
			 * @kind function
			 * @description
			 * Upload a file that will be saved as a new revision to an existing document.
			 * 
			 * @param {Number} documentId The id of the document the revision belongs to.
			 * @param {Object} file A file object that was generated by browser events.
			 * 
			 * @returns {Deferred} A promise that will resolve when the file has finished
			 * uploading, or a rejection with an error if a problem occurs during the upload process.
			 */
			uploadRevision: uploadRevision,

			/**
			 * @ngdoc property
			 * @name revisionUploadSuccess
			 * @kind property
			 * @description
			 * Event that gets emitted when a new revision has been successfully uploaded.
			 */
			get revisionUploadSuccess() { return '@AirBase/RevisionUploadSuccess'; },

			/**
			 * @ngdoc method
			 * @name revisionUploadClickHandler
			 * @kind property
			 * @description
			 * Click handler for revision upload buttons. Had to do it this way because
			 * the button click wasn't natively invoking the file input when there is an
			 * <md-icon> element within the button as well.
			 * 
			 * @param {Object} scope Angular scope element for the target directive
			 * @param {Object} element Angular element that represents the directive
			 * @param {String} targetButton HTML id of the target button
			 * @param {String} targetInput HTML id of the target file input
			 */
			revisionUploadClickHandler: revisionUploadClickHandler
		};

		function revisionUploadClickHandler(scope, element, targetButton, targetInput) {
			var buttons = element.find('button');
			var inputs = element.find('input');

			var revisionButton;
			var props = Object.keys(buttons).filter(function(k) { return k !== 'length'; });
			var prop;
			for (prop in props) {
				if (buttons[prop].getAttribute('id') === targetButton) {
					revisionButton = buttons[prop];
					break;
				}
			}

			var revisionInput;
			props = Object.keys(inputs).filter(function(k) { return k !== 'length'; });
			for (prop in props) {
				if (inputs[prop].getAttribute('id') === targetInput) {
					revisionInput = inputs[prop];
					break;
				}
			}

			if (revisionButton && revisionInput) {

				var clickHandler = function() { revisionInput.click(); };
				revisionButton.addEventListener('click', clickHandler);
				
				scope.$on('$destroy', function() {
					revisionButton.removeEventListener('click', clickHandler);
				});
			}
		}

		function startUpload(id, file, isRevision) {
			var uri = isRevision === true ? '/documents/{id}/revisions/temp' : '/f/{id}/temp';
			uri = pxUriPathService.resolvePath(uri.replace('{id}', id));

			return $http.post(uri, { FileName: file.name})
				.then(function(res) {
					return res.data;
				});
		}

		function sendChunk(file, uri, offset, chunkSize) {
			var endOffset = offset + chunkSize;
			if (endOffset >= file.size) {
				endOffset = file.size;
			}
			
			var data = readSlice(file, offset, endOffset);

			return $http.patch(uri, data, {
				headers: {
					'Content-Type': 'application/octet-stream'
				}
			});
		}

		function readSlice(file, startByte, endByte) {
			var fn = 'slice';
			
			if (file.slice) {
				fn = 'slice';
			} else if (file.mozSlice) {
				fn = 'mozSlice';
			} else if (file.webkitSlice) {
				fn = 'webkitSlice';
			}

			return file[fn](startByte, endByte);
		}

		function uploadInChunks(file, serverTempFile) {
			var patchUri = serverTempFile.Links.Location;

			return $q(function(resolve, reject) {

				(function chunk(offset, fileData) {
					if (offset <= file.size) {
						return sendChunk(file, patchUri, offset, chunkSize)
							.then(function(response) {
								chunk(offset + chunkSize, response.data);
							})
							.catch(reject);
					} else {
						resolve(fileData);
					}
				}(0));
				
			});
		}

		function finalizeUpload(id, fileData, isRevision) {
			var uri = isRevision === true ? '/documents/{id}/revisions' : '/f/{id}/documents';
			uri = pxUriPathService.resolvePath(uri.replace('{id}', id));

			return $http.post(uri, {}, {
				headers: {
					'x-sharebase-fileref': JSON.stringify(fileData)
				}
			});
		}

		/**
		 * Uploads a file to the phoenix API. The file can either be
		 * a new document or a revision to an existing document.
		 * 
		 * @private
		 * 
		 * @param {Number} id Folder id if uploading a new document and document id if uploading a revision.
		 * @param {Object} file JavaScript file object representing the file to upload.
		 * @param {Boolean} isRevision Flag that indicates if the file is a revision. Set to true to
		 *   upload a revion; defaults to false.
		 */
		function _uploadFile(id, file, isRevision) {
			return startUpload(id, file, isRevision)
				.then(function(serverTempFile) {
					return uploadInChunks(file, serverTempFile);
				})
				.then(function(fileData) {
					return finalizeUpload(id, fileData, isRevision);
				});
		}

		function uploadRevision(documentId, file) {
			return _uploadFile(documentId, file, true);
		}

		function uploadFile(folderId, file) {
			return _uploadFile(folderId, file);
		}
	}

})();


//////////////////
// WEBPACK FOOTER
// ./app/services/contentUploadService.js
// module id = 146
// module chunks = 1